// The last copy of a session the athlete opened, so it can be opened offline.
//
// Saved every time the logger loads a session online and read back only when
// that load fails for want of a network. Keyed by user, so a shared phone never
// shows one athlete's prescriptions (loads resolved against THEIR maxes) to the
// next, and cleared on logout (AuthContext).
//
// It is a snapshot and the logger says so. A %1RM resolved before a retest is
// stale until the next online load.

const PREFIX = 'lts.snapshot.v1.';

function defaultStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

// '|' never appears in an encoded username. With '.' as the separator,
// clearing user "a" also deleted the snapshots of a user named "a.b".
const userPrefix = (user) => `${PREFIX}${encodeURIComponent(user)}|`;
const keyFor = (user, programId, sessionId) => `${userPrefix(user)}${programId}|${sessionId}`;
// The calendar the home screen last read, under the same per-user prefix so
// clearFor (logout) takes it with the sessions. Cannot collide with a session
// key: those always carry a '|' after the user prefix's own.
const scheduleKey = (user) => `${userPrefix(user)}schedule`;
// A programme's phase list, for the logger's week selector offline. '#' never
// starts a programme id, so this cannot read back as a session either.
const phasesKey = (user, programId) => `${userPrefix(user)}#phases|${programId}`;

export function save(user, programId, sessionId, data, { storage = defaultStorage(), now = Date.now } = {}) {
  if (!storage || !user) return;
  try {
    storage.setItem(keyFor(user, programId, sessionId), JSON.stringify({ savedAt: now(), data }));
  } catch {
    // Full or blocked storage: the session still works online.
  }
}

/** { savedAt, data }, or null when there is no readable copy. */
export function load(user, programId, sessionId, storage = defaultStorage()) {
  if (!storage || !user) return null;
  try {
    const parsed = JSON.parse(storage.getItem(keyFor(user, programId, sessionId)) || 'null');
    return parsed && parsed.data ? parsed : null;
  } catch {
    return null;
  }
}

/** The home screen's calendar entries, saved the last time it read them online. */
export function saveSchedule(user, entries, { storage = defaultStorage(), now = Date.now } = {}) {
  if (!storage || !user || !Array.isArray(entries)) return;
  try {
    storage.setItem(scheduleKey(user), JSON.stringify({ savedAt: now(), data: entries }));
  } catch {
    // Full or blocked storage: the calendar still works online.
  }
}

/** { savedAt, data: entries }, or null. */
export function loadSchedule(user, storage = defaultStorage()) {
  if (!storage || !user) return null;
  try {
    const parsed = JSON.parse(storage.getItem(scheduleKey(user)) || 'null');
    return parsed && Array.isArray(parsed.data) ? parsed : null;
  } catch {
    return null;
  }
}

/** A programme's phases, saved the last time they were read online. */
export function savePhases(user, programId, phases, { storage = defaultStorage(), now = Date.now } = {}) {
  if (!storage || !user || !Array.isArray(phases)) return;
  try {
    storage.setItem(phasesKey(user, programId), JSON.stringify({ savedAt: now(), data: phases }));
  } catch {
    // Full or blocked storage: the selector still works online.
  }
}

/** { savedAt, data: phases }, or null. */
export function loadPhases(user, programId, storage = defaultStorage()) {
  if (!storage || !user) return null;
  try {
    const parsed = JSON.parse(storage.getItem(phasesKey(user, programId)) || 'null');
    return parsed && Array.isArray(parsed.data) ? parsed : null;
  } catch {
    return null;
  }
}

const sameLocalDay = (a, b) => a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

/**
 * When a saved copy was made, as it reads after "the copy saved ...".
 *
 * "at 14:05" for today. A prefetched copy can be days old, and "saved at 18:02"
 * on a copy from last Tuesday reads as this evening's — so anything older names
 * its day.
 */
export function describeSavedAt(savedAt, now = new Date()) {
  const saved = new Date(savedAt);
  const time = saved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (sameLocalDay(saved, now)) return `at ${time}`;
  const day = saved.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  return `on ${day} at ${time}`;
}

/** Every snapshot this user saved, and nobody else's. */
export function clearFor(user, storage = defaultStorage()) {
  if (!storage || !user) return;
  const mine = userPrefix(user);
  const doomed = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && key.startsWith(mine)) doomed.push(key);
  }
  doomed.forEach((key) => storage.removeItem(key));
}
